import React, { useEffect, FC, useState } from "react";
import { Form, Row, Col, Button } from "reactstrap";
import { Drawer, Table, InputNumber, Checkbox } from "antd";
import { FormProvider, useFieldArray, useForm } from "react-hook-form";
import FormInput from "../form-components/InputComponent";
import { Icon } from "../../components/Component";
import FormSelect from "../form-components/SelectComponent";
import FormDatePicker from "../form-components/DatePicker";
import FormTextArea from "../form-components/TextAreaComponent";
import dayjs from "dayjs";
import supplierApis from "../../api/master/supplier";
import budgetApis from "../../api/master/budget";
import contractorsApis from "../../api/master/contractors";
import boqApis from "../../api/boq/boq";
import { DeleteOutlined } from "@ant-design/icons";
import Swal from "sweetalert2";
import BulkBOMUpload from "./newBudgets/BulkBOMUpload";
import ExportToXlsx from "../../utils/exportToXlsx";
import departments from "../../api/master/departments";

interface SelectOptions {
  label: string;
  value: any;
}
interface bomLine {
  [key: string]: any; // Allow server-side fields (id, bomId, version, etc.) to pass through
  boqContractorMaterialId?: string;
  categoryId: string;
  categoryName: string;
  materialId: string;
  materialName: string;
  quantity: string;
  uomId: string;
  uomName: string;
  budgetRemaining?: string;
  allocated: string;
  budgetAllocatedQty?: string;
  totalBudgetQty?: string;
}

interface FormData {
  budgetCategoryId: string;
  customBudgetCategory: string;
  budgetLineName: string;
  budgetValue: string;
  startDate: any;
  endDate: any;
  bom: string;
  allocation: string;
  lineDescription: string;
  currencyCode: string;
  budgetFor: string;
  department: string;
  departmentName: string;
  contractorId: string;
  contractorName: string;
  bomLines: bomLine[];
  lineId?: number;
  key?: string;
  budgetLineId?: string;
  budgetId?: string;
  lineStatus?: string;
}

const MaterialDrawer: FC<any> = ({
  open,
  onClose,
  appenddata,
  update,
  formData,
  status,
  budgetStartDate,
  budgetEndDate,
  materialCategoryOptions,
  siteId,
  contractorId: budgetContractorId,
}) => {
  const [uomOptions, setUomOptions] = useState<SelectOptions[]>([]);
  const [materialData, setMaterialData] = useState<any>();
  const [materialOptions, setMaterialOptions] = useState<SelectOptions[]>([]);
  const [budgetCategoryOptions, setBudgetCategoryOptions] = useState<
    SelectOptions[]
  >([]);
  const [departmentOptions, setDepartmentOptions] = useState<SelectOptions[]>(
    []
  );
  const [contractorOptions, setContractorOptions] = useState<SelectOptions[]>(
    []
  );
  const [showCustomCategory, setShowCustomCategory] = useState(false);
  const [selectedBomRows, setSelectedBomRows] = useState<string[]>([]);
  const [uploadedCount, setUploadedCount] = useState<number>(0);
  const [showAllocationError, setShowAllocationError] = useState(false);

  const methods = useForm<FormData>({
    defaultValues: {
      budgetCategoryId: "",
      customBudgetCategory: "",
      budgetLineName: "",
      budgetValue: "",
      startDate: "",
      endDate: "",
      bom: "",
      allocation: "",
      lineDescription: "",
      currencyCode: "",
      budgetFor: "",
      department: "",
      departmentName: "",
      contractorId: "",
      contractorName: "",
      bomLines: [],
    },
    mode: "onChange",
  });

  const {
    handleSubmit,
    reset,
    control,
    watch,
    formState: { errors },
    setValue,
  } = methods;

  const budgetCategoryIdWatch = watch("budgetCategoryId");
  const budgetForWatch = watch("budgetFor");
  const allocationTypeWatch = watch("allocation");

  const { fields, append, remove, replace } = useFieldArray({
    control,
    name: "bomLines",
  });

  const startDateWatch = watch("startDate");
  const billOfMaterialWatch = watch("bom");
  const bomLinesWatch = watch("bomLines");

  const handleBomFieldChange = (index: number, fieldName: string, value: any) => {
    setValue(`bomLines.${index}.${fieldName}` as any, value);
  };

  const removeSelectedBomRows = () => {
    if (selectedBomRows.length === 0) return;
    const indexesToRemove = fields
      .map((field, index) => (selectedBomRows.includes(field.id) ? index : -1))
      .filter((i) => i !== -1)
      .sort((a, b) => b - a);
    indexesToRemove.forEach((i) => remove(i));
    setSelectedBomRows([]);
  };

  const bomColumns: any[] = [
    {
      title: (
        <Checkbox
          checked={fields.length > 0 && selectedBomRows.length === fields.length}
          indeterminate={selectedBomRows.length > 0 && selectedBomRows.length < fields.length}
          onChange={(e) => {
            if (e.target.checked) {
              setSelectedBomRows(fields.map((f) => f.id));
            } else {
              setSelectedBomRows([]);
            }
          }}
        />
      ),
      dataIndex: "select",
      key: "select",
      width: 32,
      align: "center" as const,
      className: "px-0",
      render: (_: any, record: any) => (
        <Checkbox
          checked={selectedBomRows.includes(record.id)}
          onChange={(e) => {
            if (e.target.checked) {
              setSelectedBomRows((prev) => [...prev, record.id]);
            } else {
              setSelectedBomRows((prev) => prev.filter((id) => id !== record.id));
            }
          }}
        />
      ),
    },
    {
      title: "Category",
      dataIndex: "categoryName",
      key: "categoryName",
      width: "20%",
    },
    {
      title: "Material Name",
      dataIndex: "materialName",
      key: "materialName",
      width: "20%",
    },
    {
      title: "UOM",
      dataIndex: "uomName",
      key: "uomName",
      width: "10%",
    },
    {
      title: "Available Qty",
      dataIndex: "budgetRemaining",
      key: "budgetRemaining",
      width: "12%",
      render: (_: any, record: any) => {
        const totalQty = Number(record.totalBudgetQty || 0);
        const currentQty = Number(record.quantity || 0);
        if (totalQty > 0) {
          const dynamicRemaining = Math.max(0, totalQty - currentQty);
          return <span>{dynamicRemaining}</span>;
        }
        return record.budgetRemaining || "";
      },
    },
    {
      title: <span>Qty <span style={{ color: "red" }}>*</span></span>,
      dataIndex: "quantity",
      key: "quantity",
      width: "12%",
      render: (_: any, record: any) => {
        const totalQty = Number(record.totalBudgetQty || 0);
        const maxQty = totalQty > 0
          ? totalQty
          : record.budgetRemaining ? Number(record.budgetRemaining) : undefined;

        return (
          <InputNumber
            min={1}
            placeholder="0"
            precision={2}
            controls={false}
            parser={(value: any) => value?.replace(/[^0-9.]/g, "").replace(/(\..*?)\..*/g, "$1") || ""}
            formatter={(value: any) => {
              const str = `${value}`;
              if (str.includes(".")) {
                const [int, dec] = str.split(".");
                return `${int}.${(dec || "").slice(0, 2)}`;
              }
              return str;
            }}
            onKeyDown={(e: any) => {
              if (!/[0-9.]/.test(e.key) && !["Backspace", "Delete", "ArrowLeft", "ArrowRight", "Tab", "Home", "End"].includes(e.key)) {
                e.preventDefault();
              }
              if (e.key === "." && e.target.value.includes(".")) {
                e.preventDefault();
              }
            }}
            value={record.quantity ? Number(record.quantity) : undefined}
            style={{ width: "100%" }}
            onChange={(value) => {
              if (value == null || value <= 0) {
                handleBomFieldChange(record._index, "quantity", "");
                return;
              }
              const rounded = Math.round(value * 100) / 100;
              const clamped = maxQty != null ? Math.min(Math.max(0.01, rounded), maxQty) : Math.max(0.01, rounded);
              handleBomFieldChange(record._index, "quantity", clamped.toString());
            }}
          />
        );
      },
    },
    {
      title: <span>Allocation {allocationTypeWatch === "percentage" ? "(%)" : allocationTypeWatch === "value" ? "(Value)" : ""} <span style={{ color: "red" }}>*</span></span>,
      dataIndex: "allocated",
      key: "allocated",
      width: "15%",
      render: (_: any, record: any) => (
        <InputNumber
          min={0}
          max={allocationTypeWatch === "percentage" ? 100 : undefined}
          precision={0}
          placeholder="0"
          value={record.allocated ? Number(record.allocated.toString().replace(/,/g, "")) : undefined}
          style={{ width: "100%" }}
          controls={false}
          parser={(value: any) => value?.replace(/[^0-9]/g, "") || ""}
          formatter={(value: any) => `${value}`.replace(/[^0-9]/g, "")}
          onKeyDown={(e: any) => {
            if (!/[0-9]/.test(e.key) && !["Backspace", "Delete", "ArrowLeft", "ArrowRight", "Tab", "Home", "End"].includes(e.key)) {
              e.preventDefault();
            }
          }}
          onChange={(value) => handleBomFieldChange(record._index, "allocated", value?.toString() || "")}
        />
      ),
    },
    {
      title: "",
      key: "actions",
      width: 40,
      align: "center" as const,
      render: (_: any, record: any) => (
        <DeleteOutlined
          onClick={() => {
            remove(record._index);
            setSelectedBomRows((prev) => prev.filter((id) => id !== record.id));
          }}
          style={{ color: "#ff4d4f", fontSize: 16, cursor: "pointer" }}
        />
      ),
    },
  ];

  const fetchBudgetCategories = async () => {
    try {
      const response = await budgetApis.fetchBudgetCategoryList();
      if (response.status) {
        const categories = response.response;
        if (Array.isArray(categories)) {
          const categoryOptions = categories.map((item: any) => ({
            label: item.budgetCategoryName,
            value: item.budgetCategoryId.toString(),
          }));
          setBudgetCategoryOptions(categoryOptions);
        }
      }
    } catch (error) {
      console.log("Error fetching budget categories:", error);
    }
  };

  const isOthersCategory = (categoryId: string) => {
    const selectedCategory = budgetCategoryOptions.find(
      (cat) => cat.value === categoryId
    );
    return selectedCategory?.label?.toLowerCase() === "others";
  };

  const fetchDepartments = async () => {
    try {
      const { data, status } = await departments.fetchDeptData();
      if (status) {
        const departmentOptions = data.map((item: any) => ({
          label: item.deptName,
          value: item.deptID.toString(),
        }));
        setDepartmentOptions(departmentOptions);
      }
    } catch (error) {
      console.log("Error fetching departments:", error);
    }
  };

  const fetchContractors = async () => {
    try {
      if (!siteId) return;
      const data = await contractorsApis.fetchContractorBySiteId({ id: siteId.toString() });
      if (data) {
        const options = data.data?.map((item: any) => ({
          label: item.companyName,
          value: item.contractorID.toString(),
        }));
        setContractorOptions(options || []);
      }
    } catch (error) {
      console.log("Error fetching contractors:", error);
    }
  };

  const fetchContractorBoqLines = async (contractorId: string, existingBomLines?: any[]) => {
    try {
      if (!siteId || !contractorId) return;
      const response = await boqApis.fetchContractorBoqDropdown({
        siteIds: siteId.toString(),
        contractorIds: contractorId,
      });
      const data = response?.data || response?.response || [];
      if (Array.isArray(data) && data.length > 0) {
        // Build a set of existing boqContractorMaterialIds for quick lookup (materialId is not unique — same material can have different UOMs)
        const existingBoqIds = new Set(
          (existingBomLines || []).map((line: any) => line.boqContractorMaterialId?.toString())
        );

        // Build complete bomLines array: saved lines first, then remaining API lines
        const allBomLines: any[] = [];

        // Add existing/saved BOM lines first (these will be pre-selected)
        const savedCount = existingBomLines?.length || 0;
        (existingBomLines || []).forEach((line: any) => {
          const apiItem = data.find(
            (item: any) =>
              item.boqContractorMaterialId?.toString() === line.boqContractorMaterialId?.toString()
          );
          const savedQty = Number(line.quantity || 0);
          const remaining = Number(apiItem?.budgetRemainingQty || apiItem?.budgetRemaining || 0);
          const totalBudgetQty = remaining + savedQty;
          allBomLines.push({
            boqContractorMaterialId: line.boqContractorMaterialId || apiItem?.boqContractorMaterialId?.toString() || "",
            categoryId: line.categoryId?.toString() || "",
            categoryName: line.categoryName || "",
            materialId: line.materialId?.toString() || "",
            materialName: line.materialName || "",
            quantity: savedQty.toString(),
            uomId: line.uomId?.toString() || "",
            uomName: line.uomName || "",
            budgetRemaining: remaining.toString(),
            allocated: line.allocated?.toString() || "",
            budgetAllocatedQty: line.budgetAllocatedQty?.toString() || apiItem?.budgetAllocatedQty?.toString() || "",
            totalBudgetQty: totalBudgetQty.toString(),
          });
        });

        // Add remaining BOQ lines that are NOT in existing saved lines
        data.forEach((item: any) => {
          const boqId = item.boqContractorMaterialId?.toString() || "";
          const matId = item.materialId?.toString() || item.materialID?.toString() || "";
          if (boqId && !existingBoqIds.has(boqId)) {
            allBomLines.push({
              boqContractorMaterialId: boqId || item.materialID?.toString() || "",
              categoryId: item.categoryId?.toString() || "",
              categoryName: item.categoryName || "",
              materialId: matId,
              materialName: item.materialName || item.productName || "",
              quantity: "",
              uomId: item.uomId?.toString() || item.primaryUomId?.toString() || "",
              uomName: item.uomName || item.primaryUomName || "",
              budgetRemaining: item.budgetRemainingQty?.toString() || item.budgetRemaining?.toString() || "",
              allocated: "",
              budgetAllocatedQty: item.budgetAllocatedQty?.toString() || "",
            });
          }
        });

        // Set all BOM lines at once using replace to avoid race conditions
        replace(allBomLines);

        // Auto-select the saved BOM lines
        if (savedCount > 0) {
          setUploadedCount(savedCount);
        }
      }
    } catch (error) {
      console.log("Error fetching contractor BOQ lines:", error);
    }
  };

  useEffect(() => {
    if (isOthersCategory(budgetCategoryIdWatch)) {
      setShowCustomCategory(true);
    } else {
      setShowCustomCategory(false);
      setValue("customBudgetCategory", "");
    }
  }, [budgetCategoryIdWatch, budgetCategoryOptions, setValue]);

  useEffect(() => {
    if (formData.item && open) {
      reset({
        budgetCategoryId: formData.item.budgetCategoryId,
        customBudgetCategory: formData.item.customBudgetCategory || "",
        budgetLineName: formData.item.budgetLineName,
        department: formData.item.department,
        departmentName: formData.item.departmentName,
        budgetValue: formData.item.budgetValue
          ? new Intl.NumberFormat("en-IN", {}).format(formData.item.budgetValue)
          : "",
        startDate: dayjs(formData.item.startDate),
        endDate: dayjs(formData.item.endDate),
        bom: formData.item.bom,
        currencyCode: formData.item.currencyCode,
        allocation: formData.item.allocation,
        lineDescription: formData.item.lineDescription,
        budgetFor: formData.item.budgetFor || "",
        contractorId: formData.item.contractorId?.toString() || budgetContractorId?.toString() || "",
        contractorName: formData.item.contractorName || "",
        bomLines: [],
      });
      setSelectedBomRows([]);
      setShowAllocationError(false);
      // When editing with BOM, fetch all BOQ lines and merge with saved ones
      const cId = formData.item.contractorId?.toString() || budgetContractorId?.toString() || "";
      if (formData.item.bom === "Y" && cId) {
        fetchContractorBoqLines(cId, formData.item.bomLines || []);
      }
    } else if (!formData.item && open) {
      reset({
        budgetCategoryId: "",
        customBudgetCategory: "",
        budgetLineName: "",
        budgetValue: "",
        startDate: "",
        endDate: "",
        bom: "",
        allocation: "",
        lineDescription: "",
        currencyCode: "",
        budgetFor: "",
        department: "",
        departmentName: "",
        contractorId: budgetContractorId?.toString() || "",
        contractorName: "",
        bomLines: [],
      });
      setShowCustomCategory(false);
      setSelectedBomRows([]);
      setShowAllocationError(false);
    }
  }, [formData.item, open]);

  useEffect(() => {
    fetchBudgetCategories();
    fetchDepartments();
    fetchContractors();
  }, []);

  // After contractor options load, ensure the item's contractor is in the list and re-apply value
  useEffect(() => {
    if (!formData.item || !open) return;
    const cId = formData.item.contractorId?.toString() || budgetContractorId?.toString() || "";
    const cName = formData.item.contractorName || "";
    if (cId) {
      // If the contractor from the item is not in the options, inject it
      if (cName && !contractorOptions.some((opt) => opt.value === cId)) {
        setContractorOptions((prev) => [...prev, { label: cName, value: cId }]);
      }
      setValue("contractorId", cId);
    }
  }, [contractorOptions, formData.item, open]);

  useEffect(() => {
    if (departmentOptions.length > 0 && formData.item && open) {
      const deptId = formData.item.department?.toString() || "";
      if (deptId) {
        setValue("department", deptId);
      }
    }
  }, [departmentOptions]);

  useEffect(() => {
    if (budgetForWatch === "Material") {
      // BOM lines populated when contractor is selected
    } else if (budgetForWatch === "Service/Work") {
      setValue("bom", "N");
      reset({
        ...methods.getValues(),
        bom: "N",
        allocation: "",
        bomLines: [],
      });
    }
  }, [budgetForWatch]);

  // Reset allocation values when BOM Allocation type changes
  const prevAllocationType = React.useRef(allocationTypeWatch);
  useEffect(() => {
    if (prevAllocationType.current && allocationTypeWatch && prevAllocationType.current !== allocationTypeWatch && fields.length > 0) {
      const currentValues = methods.getValues();
      const resetBomLines = currentValues.bomLines.map((line: any) => ({
        ...line,
        allocated: "",
      }));
      reset({ ...currentValues, bomLines: resetBomLines });
    }
    prevAllocationType.current = allocationTypeWatch;
  }, [allocationTypeWatch]);

  // Auto-select uploaded BOM lines once fields are updated
  useEffect(() => {
    if (uploadedCount > 0 && fields.length >= uploadedCount) {
      const uploadedFieldIds = fields.slice(0, uploadedCount).map((f) => f.id);
      setSelectedBomRows(uploadedFieldIds);
      setUploadedCount(0);
    }
  }, [fields, uploadedCount]);

  const fetchUom = async (materialId: any, materialData: any, index: any) => {
    if (!materialId) return [];
    try {
      const materialUom = materialData?.find(
        (item: any) => item.materialId == materialId
      );
      if (!materialUom) return;
      const _uomOptions = [
        {
          value: materialUom?.primaryUomId,
          label: materialUom?.PrimaryUomName,
        },
      ];
      if (
        materialUom?.secondaryUomId &&
        materialUom?.primaryUomId != materialUom?.secondaryUomId &&
        materialUom?.PrimaryUomName != materialUom?.secondaryUomName
      ) {
        _uomOptions.push({
          value: materialUom?.secondaryUomId,
          label: materialUom?.secondaryUomName,
        });
      }
      setUomOptions(_uomOptions);
      return _uomOptions;
    } catch (error) {
      console.log(error);
    }
  };

  const fetchMaterials = async (categoryId: any, fields: any) => {
    if (!categoryId) return { data: [], materialOption: [] };
    try {
      const { data, status } = await supplierApis.fetchCategoryProducts(
        categoryId
      );
      if (status) {
        const materialOption = data.map((item: any) => ({
          label: item?.materialName,
          value: item?.materialId?.toString(),
        }));

        let filteredOptions = materialOption;
        if (Array.isArray(fields)) {
          const createdMaterialIds = fields.map(
            (field: any) => field.materialId
          );
          filteredOptions = materialOption.filter(
            (option: any) =>
              !createdMaterialIds || !createdMaterialIds.includes(option.value)
          );
        }

        setMaterialData(data);
        setMaterialOptions(filteredOptions);
        return {
          data,
          materialOption,
        };
      }
    } catch (error) {
      console.log(error);
      return {};
    }
  };

  const handleBulkUpload = (uploadedBomLines: any[]) => {
    // Clear existing BOM lines first
    reset({
      ...methods.getValues(),
      bomLines: [],
    });

    // Track how many are from the upload (matched lines come first)
    const count = uploadedBomLines.filter((b: any) => b._fromUpload).length;
    setUploadedCount(count);

    // Add all uploaded BOM lines
    uploadedBomLines.forEach((bomLine: any) => {
      append({
        boqContractorMaterialId: bomLine.boqContractorMaterialId || "",
        categoryId: bomLine.categoryId,
        categoryName: bomLine.categoryName,
        materialId: bomLine.materialId,
        materialName: bomLine.materialName,
        quantity: bomLine.quantity,
        uomId: bomLine.uomId,
        uomName: bomLine.uomName,
        budgetRemaining: bomLine.budgetRemaining || "",
        allocated: bomLine.allocated,
        budgetAllocatedQty: bomLine.budgetAllocatedQty || "",
      });
    });
  };

  const exportBomLines = () => {
    // If user has selected rows, export only selected; otherwise export all
    const indicesToExport = selectedBomRows.length > 0
      ? fields.map((field, index) => (selectedBomRows.includes(field.id) ? index : -1)).filter((i) => i !== -1)
      : fields.map((_, index) => index);

    const bomLinesData = indicesToExport.map((index) => {
      const line = methods.getValues(`bomLines.${index}`);
      return {
        "Material ID": line.materialId || "",
        "Category Name": line.categoryName || "",
        "Material Name": line.materialName || "",
        "UOM": line.uomName || "",
        "Budget Remaining": line.budgetRemaining || "",
        "Quantity": line.quantity || "",
        "Allocation": line.allocated || "",
      };
    });
    if (bomLinesData.length === 0) {
      Swal.fire({
        icon: "warning",
        title: "No Data",
        text: "No BOM lines available to export.",
      });
      return;
    }
    ExportToXlsx(bomLinesData, "BOM_Lines");
  };

  const submitForm = (dataForm: FormData) => {
    try {
      setShowAllocationError(true);
      // Only selected BOM lines should be saved
      if (dataForm.bom === "Y" && fields.length > 0) {
        if (selectedBomRows.length === 0) {
          Swal.fire({
            icon: "error",
            title: "Validation Error",
            text: "Please select at least one BOM line before adding the budget line.",
          });
          return;
        }
        const selectedIndices = fields
          .map((field, index) => (selectedBomRows.includes(field.id) ? index : -1))
          .filter((i) => i !== -1);
        dataForm.bomLines = selectedIndices.map((i) => dataForm.bomLines[i]);
      }

      // Clean up bomLines payload - remove display-only fields and empty uom fields
      if (dataForm.bomLines) {
        dataForm.bomLines = dataForm.bomLines.map((line: any) => {
          const { budgetRemaining, _fromUpload, totalBudgetQty, ...rest } = line;
          if (!rest.uomId) {
            delete rest.uomId;
            delete rest.uomName;
          }
          return rest;
        });
      }

      const budgetValue = parseFloat(dataForm.budgetValue.replace(/,/g, ""));
      const totalAllocated = dataForm.bomLines.reduce((sum: any, item: any) => {
        return (
          sum +
          parseFloat(item?.allocated?.toString()?.replace(/,/g, "") || "0")
        );
      }, 0);
      if (dataForm.allocation === "value") {
        if (dataForm.bom === "Y" && totalAllocated !== budgetValue) {
          Swal.fire({
            icon: "error",
            title: "Validation Error",
            text: totalAllocated > budgetValue
              ? "Total Allocated value in BOM Lines cannot exceed the Budget Value."
              : "Total Allocated value in BOM Lines must be equal to the Budget Value.",
          });
          return;
        }
      } else if (dataForm.allocation === "percentage") {
        const totalPercentage = totalAllocated;
        if (dataForm.bom === "Y" && totalPercentage > 100) {
          Swal.fire({
            icon: "error",
            title: "Validation Error",
            text: "Total Allocated % in BOM Lines cannot exceed more than 100%.",
          });
          return;
        }
        if (dataForm.bom === "Y" && totalPercentage < 100) {
          Swal.fire({
            icon: "error",
            title: "Validation Error",
            text: "Total Allocated % in BOM Lines must be equal to 100%.",
          });
          return;
        }
      }

      // Handle budget category validation
      if (
        isOthersCategory(dataForm.budgetCategoryId) &&
        !dataForm.customBudgetCategory.trim()
      ) {
        Swal.fire({
          icon: "error",
          title: "Validation Error",
          text: "Please enter a custom budget category name.",
        });
        return;
      }

      // Prepare budget category data
      const budgetCategoryData = isOthersCategory(dataForm.budgetCategoryId)
        ? {
            budgetCategoryId: null,
            budgetCategoryName: "Others",
            customBudgetCategory: dataForm.customBudgetCategory,
          }
        : {
            budgetCategoryId: parseInt(dataForm.budgetCategoryId),
            budgetCategoryName:
              budgetCategoryOptions.find(
                (cat) => cat.value == dataForm.budgetCategoryId
              )?.label || "",
            customBudgetCategory: null,
          };

      const updatedData = {
        ...dataForm,
        ...budgetCategoryData,
      };

      if (updatedData.bom === "N") {
        updatedData.bomLines = [];
        delete (updatedData as any).allocation;
      }
      delete (updatedData as any).boms;
      if (updatedData.bomLines.length > 0) {
        const originalBomLines = formData.item?.bomLines || [];
        updatedData.bomLines = updatedData.bomLines.map((bom: any, index: number) => {
          // Merge with original bomLine data to preserve server-side fields (id, bomId, etc.)
          const originalBom = originalBomLines.find(
            (ob: any) => ob.materialId?.toString() === bom.materialId?.toString() && ob.categoryId?.toString() === bom.categoryId?.toString()
          ) || originalBomLines[index] || {};
          const merged = {
            ...originalBom,
            ...bom,
          };
          merged.quantity = bom.quantity;
          merged.allocated = parseFloat(
            bom?.allocated?.toString()?.replace(/,/g, "") || "0"
          );
          merged.uomId = bom.uomId ? Number(bom.uomId) : undefined;
          return merged;
        });
      }
      updatedData.budgetValue = (
        parseFloat(updatedData.budgetValue.replace(/,/g, "")) || 0
      ).toString();

      updatedData.startDate = dayjs(updatedData.startDate).format("YYYY-MM-DD");
      updatedData.endDate = dayjs(updatedData.endDate).format("YYYY-MM-DD");

      if (formData.action === "edit") {
        updatedData.lineId = formData.index;
        // Preserve budgetLineId, budgetId, and lineStatus from the original item if they exist
        if (formData.item.budgetLineId) {
          updatedData.budgetLineId = formData.item.budgetLineId;
        }
        if (formData.item.budgetId) {
          updatedData.budgetId = formData.item.budgetId;
        }
        if (formData.item.lineStatus) {
          updatedData.lineStatus = formData.item.lineStatus;
        }
        update(formData.index, updatedData);
      } else {
        // Don't add lineId for new items - backend will assign it
        updatedData.key = updatedData.budgetLineName || `new_${Date.now()}`;
        appenddata(updatedData);
      }

      onClose();
    } catch (error) {
      console.log("Error:", error);
    }
  };

  const closeDrawer = () => {
    onClose();
    reset();
    setSelectedBomRows([]);
  };

  return (
    <Drawer
      title={
        formData?.action === "edit"
          ? "Update Budget Line"
          : "Add New Budget Line"
      }
      placement="right"
      onClose={closeDrawer}
      open={open}
      width={1200}
      destroyOnClose={true}
    >
      <FormProvider {...methods}>
        <Form onSubmit={handleSubmit(submitForm)}>
          <Row className="gy-4">
            <Col lg="6">
              <FormInput
                name="budgetLineName"
                label="Budget Line Name"
                required={true}
                placeholder="Enter Budget Line Name"
              />
            </Col>
            <Col lg="6">
              <FormSelect
                name="budgetFor"
                label="Budget For"
                options={[
                  { label: "Material", value: "Material" },
                  { label: "Service/Work", value: "Service/Work" },
                ]}
                required={true}
                placeholder="Select Budget For"
              />
            </Col>

            <Col lg="6">
              <FormInput
                min={1}
                name="budgetValue"
                type="text"
                label="Budget Value"
                required={true}
                placeholder="Enter Budget Value"
                pattern={/^[0-9,]+$/}
                onInput={(e: any) => {
                  const val = e.target.value.replace(/[^\d.]/g, "");
                  if (val.length > 0 && !/^[0-9]+(\.[0-9]+)?$/.test(val)) {
                    e.target.value = val.replace(/[^\d.]/g, "");
                  } else {
                    const n = Number(val);
                    const formattedValue = isNaN(n)
                      ? ""
                      : new Intl.NumberFormat("en-IN", {}).format(n);
                    e.target.value = formattedValue;
                  }
                }}
              />
            </Col>

            <Col lg="6">
              <FormDatePicker
                name="startDate"
                label="Planned Start Date"
                required={true}
                placeholder="Select Start Date"
                disabledDate={(current: any) => {
                  return (
                    current && current < dayjs(budgetStartDate).startOf("day")
                  );
                }}
              />
            </Col>

            <Col lg="6">
              <FormDatePicker
                name="endDate"
                label="Planned End Date"
                required={true}
                placeholder="Select End Date"
                disabled={!startDateWatch}
                disabledDate={(current: any) => {
                  return (
                    (current &&
                      current < dayjs(startDateWatch).startOf("day")) ||
                    (current && current > dayjs(budgetEndDate).endOf("day"))
                  );
                }}
              />
            </Col>

            <Col lg="6">
              <FormSelect
                name="budgetCategoryId"
                label="Budget Category"
                options={budgetCategoryOptions}
                required={true}
                placeholder="Select Budget Category"
              />
            </Col>

            {showCustomCategory && (
              <Col lg="6">
                <FormInput
                  name="customBudgetCategory"
                  label="Custom Budget Category"
                  required={true}
                  placeholder="Enter Custom Budget Category"
                />
              </Col>
            )}

            <Col lg="6">
              <FormSelect
                name="contractorId"
                label="Contractor"
                required={true}
                options={contractorOptions}
                disabled={formData.action === "edit"}
                placeholder="Select Contractor"
                onChange={(value) => {
                  const contractorName =
                    contractorOptions.find(
                      (opt: any) => opt.value === value
                    )?.label || "";
                  setValue("contractorName", contractorName);
                  if (budgetForWatch === "Material" && value) {
                    fetchContractorBoqLines(value);
                  }
                }}
              />
            </Col>

            <Col lg="6">
              <FormSelect
                name="department"
                label="Department"
                options={departmentOptions}
                required={true}
                placeholder="Select Department"
                onChange={(value) => {
                  const deptName =
                    departmentOptions.find((dept: any) => dept.value === value)
                      ?.label || "";
                  setValue("departmentName", deptName);
                }}
              />
            </Col>

            {budgetForWatch === "Material" && (
              <Col lg="6">
                <FormSelect
                  name="bom"
                  label="Bill Of Material (BOM)"
                  options={[
                    { label: "Yes", value: "Y" },
                    { label: "No", value: "N" },
                  ]}
                  required={true}
                  placeholder="Select BOM"
                  onChange={(e) => {
                    if (e === "Y" && fields.length === 0) {
                      const cId = methods.getValues("contractorId");
                      if (cId) {
                        fetchContractorBoqLines(cId);
                      }
                    } else if (e === "N") {
                      reset({
                        ...methods.getValues(),
                        bomLines: [],
                      });
                    }
                  }}
                />
              </Col>
            )}
            {billOfMaterialWatch === "Y" && (
              <Col lg="6">
                <FormSelect
                  disabled={
                    formData.action !== "add" && formData.item?.allocation
                  }
                  name="allocation"
                  label="BOM Allocation"
                  options={[
                    { label: "Value", value: "value" },
                    { label: "%", value: "percentage" },
                  ]}
                  required={true}
                  placeholder="Select BOM Allocation"
                />
              </Col>
            )}
            <Col lg="12">
              <FormTextArea
                name="lineDescription"
                label="Budget Line Description"
                required={true}
                placeholder="Enter Budget Line Description"
              />
            </Col>
          </Row>

          {billOfMaterialWatch === "Y" && (
            <div style={{ marginTop: "20px" }}>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="card-title mb-0">BOM Lines</h6>
                <div className="d-flex align-items-center gap-2">
                  <BulkBOMUpload
                    onUpload={handleBulkUpload}
                    materialCategoryOptions={materialCategoryOptions || []}
                    allocation={watch("allocation")}
                    budgetValue={
                      parseFloat(
                        watch("budgetValue")?.toString()?.replace(/,/g, "") ||
                          "0"
                      ) || 0
                    }
                    existingBomLines={fields}
                    siteId={siteId?.toString()}
                    contractorId={watch("contractorId")}
                    disabled={!watch("allocation") || !watch("contractorId") || !watch("bom")}
                  />
                  <Button
                    size="sm"
                    color="light"
                    className="btn btn-white btn-outline-light"
                    onClick={exportBomLines}
                    type="button"
                  >
                    <Icon name="download-cloud" />
                    <span>Export</span>
                  </Button>
                  {selectedBomRows.length > 0 && (
                    <Button
                      size="sm"
                      color="danger"
                      onClick={removeSelectedBomRows}
                      type="button"
                    >
                      <DeleteOutlined style={{ marginRight: 6 }} />
                      Remove Selected ({selectedBomRows.length})
                    </Button>
                  )}
                </div>
              </div>

              <div style={{
                border: "1px solid #f0f0f0",
                borderRadius: "8px",
                overflow: "auto",
              }}>
                <Table
                  columns={bomColumns}
                  dataSource={fields.map((field, index) => ({
                    ...field,
                    ...(bomLinesWatch?.[index] || {}),
                    key: field.id,
                    _index: index,
                  })).sort((a, b) => {
                    const aSelected = selectedBomRows.includes(a.id) ? 0 : 1;
                    const bSelected = selectedBomRows.includes(b.id) ? 0 : 1;
                    return aSelected - bSelected;
                  })}
                  rowKey="key"
                  pagination={false}
                  size="middle"
                  bordered={false}
                  scroll={{ x: "max-content" }}
                  locale={{ emptyText: "No BOM lines added yet" }}
                />
              </div>

              {showAllocationError && selectedBomRows.length > 0 && (() => {
                const selectedIndices = fields
                  .map((field, idx) => (selectedBomRows.includes(field.id) ? idx : -1))
                  .filter((i) => i !== -1);
                const totalAllocated = selectedIndices.reduce((sum, idx) => {
                  const val = methods.getValues(`bomLines.${idx}.allocated`);
                  return sum + (parseFloat(val?.toString()?.replace(/,/g, "") || "0") || 0);
                }, 0);
                const bv = parseFloat(watch("budgetValue")?.toString()?.replace(/,/g, "") || "0") || 0;
                const isValue = allocationTypeWatch === "value";
                const isPercentage = allocationTypeWatch === "percentage";
                const hasError = (isValue && totalAllocated !== bv) || (isPercentage && totalAllocated !== 100);

                return hasError ? (
                  <div style={{ color: "#ff4d4f", fontSize: 13, marginTop: 8 }}>
                    {isValue
                      ? totalAllocated > bv
                        ? `Selected BOM lines total allocation (${totalAllocated.toLocaleString()}) exceeds Budget Value (${bv.toLocaleString()})`
                        : `Selected BOM lines total allocation (${totalAllocated.toLocaleString()}) is below Budget Value (${bv.toLocaleString()})`
                      : totalAllocated > 100
                        ? `Selected BOM lines total allocation (${totalAllocated}%) exceeds 100%`
                        : `Selected BOM lines total allocation (${totalAllocated}%) is below 100%`}
                  </div>
                ) : null;
              })()}
            </div>
          )}

          <Row className="mt-4">
            <Col lg="12">
              <ul className="align-center flex-wrap flex-sm-nowrap gx-4 gy-2">
                <li>
                  <Button color="primary" type="submit">
                    {formData?.action === "edit"
                      ? "Update Budget Line"
                      : "Add Budget Line"}
                  </Button>
                </li>
                <li>
                  <Button
                    color="secondary"
                    onClick={closeDrawer}
                    className="btn-dim btn btn-secondary"
                  >
                    Cancel
                  </Button>
                </li>
              </ul>
            </Col>
          </Row>
        </Form>
      </FormProvider>
    </Drawer>
  );
};

export default MaterialDrawer;
